import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import api, { unwrap } from '../api/client';
import './AssistantPanel.css';

/* Assistant — fenêtre de chat Claude (prototype lecture seule).
   Bouton flottant en bas à droite ; panneau ancré au-dessus.
   L'historique (texte seul) est gardé côté client et renvoyé entier
   à chaque tour ; les appels d'outils restent côté serveur. */

const sparkIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
  </svg>
);

export default function AssistantPanel({ tenant, year }) {
  const { t } = useTranslation();
  const p = useP();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // [{role, content}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  // Changer d'entreprise repart d'une conversation vierge.
  useEffect(() => { setMessages([]); setError(null); }, [tenant?.id]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy, open]);

  async function send() {
    const text = input.trim();
    if (!text || busy || !tenant) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    setError(null);
    const res = await unwrap(api.post(`/assistant/${tenant.id}/chat`, {
      year,
      messages: next,
    }));
    setBusy(false);
    if (res.error || !res.data) {
      setError(typeof res.error === 'string' ? res.error : p('Erreur assistant.'));
      return;
    }
    setMessages([...next, { role: 'assistant', content: res.data.reply }]);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!tenant?.assistant_enabled) return null;

  const hint = p("Prototype en lecture seule — pose une question sur le P&L, par ex. « quel est l'EBITDA {{year}} ? » ou « comment évoluent les salaires mois par mois ? ».")
    .replace(/\{\{year\}\}/g, String(year));

  return (
    <>
      {open && (
        <div className="asst-panel">
          <div className="asst-head">
            <span className="asst-title">{p('Assistant')}</span>
            <span className="asst-scope">{tenant.name} · {year}</span>
            <button className="asst-close" onClick={() => setOpen(false)} title={t('common:actions.close')}>×</button>
          </div>
          <div className="asst-body" ref={bodyRef}>
            {messages.length === 0 && (
              <div className="asst-hint">{hint}</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`asst-msg asst-msg--${m.role}`}>
                {m.content}
              </div>
            ))}
            {busy && <div className="asst-msg asst-msg--assistant asst-msg--busy">…</div>}
            {error && <div className="asst-error">{error}</div>}
          </div>
          <div className="asst-input">
            <textarea
              rows={2}
              value={input}
              placeholder={p('Ta question…')}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
            />
            <button onClick={send} disabled={busy || !input.trim()}>{p('Envoyer')}</button>
          </div>
        </div>
      )}
      <button
        className={`asst-fab${open ? ' is-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={p('Assistant Palestre')}
      >
        {sparkIcon}
      </button>
    </>
  );
}
